import React, {useEffect, useMemo, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import {makeStyles} from '@material-ui/core/styles';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import {AddCircleOutlineOutlined} from '@material-ui/icons';
import DataGrid from '../../../shared/datagrid/DataGrid.tsx';
import BlockUI from '../../../shared/blockui/BlockUI.tsx';
import TextButton from '../../../shared/buttons/TextButton.tsx';
import {criarColunaOpcoes} from '../../components/ColunaOpcoes.jsx';
import FotoPessoa from '../../components/cadastro/FotoPessoa.jsx';
import {ouTraco} from '../../components/inscricao/documentoInscricao.js';
import {PAGINACAO_PADRAO, textoSemRegistros} from '../../components/paginacao.js';
import useFetch from '../../../../hooks/useFetch.js';
import {URL_ATENDENTES_PESSOAS} from '../../../../constants/index.js';
import {buttonTableCell} from '../../../../utils/styles.js';

const PARAMS_VAZIOS = {};
const FILTER_OPTIONS = {filterByColumn: true};
const TABLE_CONTAINER_PROPS = {component: 'div'};

const useStyles = makeStyles(theme => ({
    ...buttonTableCell(theme),
    paper: {
        width: 800,
        maxWidth: '100%'
    },
    contentRoot: {
        overflowY: 'initial'
    },
    area: {
        minHeight: theme.spacing(12)
    },
    colunaFoto: {
        width: theme.spacing(9)
    }
}));

/*
 * Pessoas com acesso ao sistema que ainda não são atendentes (lista completa; filtro e
 * paginação no cliente). O diálogo fica aberto: cada "Adicionar" chama `onSelecionar(pessoa)`
 * (quem grava é a página) e, quando a página incrementa `atualizacao` após gravar, a lista é
 * buscada de novo e a pessoa adicionada deixa de aparecer.
 */
export default function DialogSelecionarPessoaAtendente(props) {
    const {open, onClose, onSelecionar, atualizacao = 0, ocupado = false} = props;
    const classes = useStyles();
    const [fetch, carregando] = useFetch({returnInnerLoadingState: true});
    const [itens, setItens] = useState([]);

    // A credencial renova a cada resposta: os callbacks usam sempre o fetch do render mais recente.
    const fetchRef = useRef(fetch);
    fetchRef.current = fetch;
    // O callback das colunas (memorizadas) lê a prop atual por ref.
    const selecionarRef = useRef(null);
    selecionarRef.current = onSelecionar;

    useEffect(() => {
        if (open) {
            fetchRef.current(URL_ATENDENTES_PESSOAS, PARAMS_VAZIOS, dados => setItens(Array.isArray(dados) ? dados : []));
        }
    }, [open, atualizacao]);

    const colunas = useMemo(() => [
        {
            label: 'Foto',
            filterable: false,
            className: classes.colunaFoto,
            valueGetter: (valor, row) => <FotoPessoa urlFoto={row.urlFoto} nome={row.nome}/>
        },
        {label: 'Nome da pessoa', field: 'nome', filterable: true, valueGetter: valor => ouTraco(valor)},
        criarColunaOpcoes(row => [
            {
                titulo: 'Adicionar',
                icone: <AddCircleOutlineOutlined/>,
                // Enquanto grava, evita clique repetido.
                desabilitado: ocupado,
                onClick: () => selecionarRef.current(row)
            }
        ], {className: classes.buttonTableCell})
    ], [classes, ocupado]);

    return (
        <Dialog
            open={open}
            onClose={onClose}
            scroll='body'
            aria-labelledby='dialog-selecionar-pessoa-atendente-titulo'
            classes={{paper: classes.paper}}>
            <DialogTitle id='dialog-selecionar-pessoa-atendente-titulo'>Adicionar atendente</DialogTitle>
            <DialogContent classes={{root: classes.contentRoot}}>
                <div className={classes.area}>
                    <BlockUI loading={carregando}>
                        <DataGrid
                            rows={itens}
                            comparatorKeys={['idPessoa']}
                            columns={colunas}
                            filterOptions={FILTER_OPTIONS}
                            paginate
                            paginateOptions={PAGINACAO_PADRAO}
                            tableContainerProps={TABLE_CONTAINER_PROPS}
                            noRecordsText={textoSemRegistros(carregando, 'Nenhuma pessoa disponível.')}/>
                    </BlockUI>
                </div>
            </DialogContent>
            <DialogActions>
                <TextButton onClick={onClose}>Fechar</TextButton>
            </DialogActions>
        </Dialog>
    );
}

DialogSelecionarPessoaAtendente.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    /* Recebe a pessoa escolhida ({idPessoa, nome, urlFoto}); quem grava é a página. */
    onSelecionar: PropTypes.func.isRequired,
    /* Incrementada pela página após gravar: refaz a busca da lista. */
    atualizacao: PropTypes.number,
    /* Desabilita a ação enquanto a tela grava. */
    ocupado: PropTypes.bool
};
